import { SetDetail } from 'store/services/trade/tradeApi';
import { MTGCard } from 'types/interfaces/mtgCard';

interface FinishAvailabilityResult {
  availableInFoil: boolean;
  availableInNonFoil: boolean;
  defaultFoil: boolean;
}

export interface FinishOption {
  value: 'nonfoil' | 'foil';
  label: string;
}

/** Card- or printing-shaped API object; only the finish-relevant fields matter. */
export interface FinishSource {
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  foil?: boolean;
  nonFoil?: boolean;
  price?: unknown;
}

/**
 * Builds the finish dropdown options for the card detail page.
 *
 * Precedence per finish (highest wins):
 *   1. Backend-computed availableInFoil / availableInNonFoil — derived from the card's
 *      finishes array; etched counts as foil. Trusted even when price data disagrees
 *      (an unpriced finish still exists; a shared-fallback price on a missing finish
 *      must not surface it).
 *   2. Legacy derivation for responses that predate the flags: raw foil/nonFoil booleans,
 *      overridden by price presence when any price is > 0. Etched price counts toward
 *      foil (the site models etched as a foil finish).
 *
 * Never returns an empty list — with zero data both finishes are offered.
 */
export function getFinishOptions(source: FinishSource | undefined): FinishOption[] {
  const priceData: any = Array.isArray((source as any)?.price) && (source as any).price.length > 0
    ? (source as any).price[0]
    : (source as any)?.price;

  let legacyNonFoil = source?.nonFoil !== undefined ? source.nonFoil : true;
  let legacyFoil = source?.foil !== undefined ? source.foil : true;

  if (priceData) {
    const hasNonFoilPrice = priceData.usd != null && Number(priceData.usd) > 0;
    const hasFoilPrice = priceData.usdFoil != null && Number(priceData.usdFoil) > 0;
    const hasEtchedPrice = priceData.usdEtched != null && Number(priceData.usdEtched) > 0;

    if (hasNonFoilPrice || hasFoilPrice || hasEtchedPrice) {
      legacyNonFoil = hasNonFoilPrice;
      legacyFoil = hasFoilPrice || hasEtchedPrice;
    }
  }

  const availableInNonFoil = source?.availableInNonFoil ?? legacyNonFoil;
  const availableInFoil = source?.availableInFoil ?? legacyFoil;

  const types: FinishOption[] = [];
  if (availableInNonFoil) {
    types.push({ value: 'nonfoil', label: 'Non-Foil' });
  }
  if (availableInFoil) {
    types.push({ value: 'foil', label: 'Foil' });
  }

  if (types.length === 0) {
    types.push({ value: 'nonfoil', label: 'Non-Foil' });
    types.push({ value: 'foil', label: 'Foil' });
  }

  return types;
}

/**
 * Determines foil/non-foil availability and the default foil selection for a card.
 *
 * Fallback precedence (highest wins):
 *   1. SetDetail fields — backend-computed from the card's finishes array. Always trusted
 *      even if price data seems to contradict (an unpriced card ≠ a card that doesn't exist
 *      in that finish).
 *   2. Price data — used only when SetDetail fields are undefined. If any price field is
 *      non-null, a non-null price > 0 means that finish exists.
 *   3. card.foil flag / safe default — last resort. For non-foil availability we default to
 *      true (preserves safe fallback when we have zero data).
 */
export function getFinishAvailability(
  matchingSetDetail: SetDetail | undefined,
  card: MTGCard | undefined,
  acceptFoil: boolean,
  acceptNonFoil: boolean,
): FinishAvailabilityResult {
  // Extract price data from card
  const priceData = Array.isArray(card?.price) && card!.price.length > 0
    ? card!.price[0]
    : (card?.price as any);

  const hasPriceData = priceData && (
    priceData.usd != null || priceData.usdFoil != null || priceData.usdEtched != null
  );

  // Determine physical availability using the 3-level fallback chain
  const availableInFoil = matchingSetDetail?.availableInFoil
    ?? (hasPriceData ? (priceData.usdFoil != null && Number(priceData.usdFoil) > 0) : (card?.foil ?? false));

  const availableInNonFoil = matchingSetDetail?.availableInNonFoil
    ?? (hasPriceData ? (priceData.usd != null && Number(priceData.usd) > 0) : true);

  // Combine physical availability with user preferences
  const canOfferFoil = availableInFoil && acceptFoil;
  const canOfferNonFoil = availableInNonFoil && acceptNonFoil;

  // Default to non-foil when possible; only default to foil when non-foil isn't an option
  let defaultFoil = false;
  if (!canOfferNonFoil && canOfferFoil) {
    defaultFoil = true;
  }

  return { availableInFoil, availableInNonFoil, defaultFoil };
}
